import type { CopyField, CopyTexts } from '@/lib/api';

/**
 * Textos por idioma: {es: {name, summary...}, en: {...}}.
 *
 * Es la caché editable en memoria: lo que el usuario escribe en los campos vive
 * aquí hasta que pulsa Guardar. En la app original ese papel lo hacían los
 * propios <input> del DOM y `languagesMap` guardaba solo lo ya persistido; al
 * pasar a React necesitamos una única fuente de verdad para pintar, y esta lo es.
 */
export type MapaIdiomas = Record<string, CopyTexts>;

/** Los 20 idiomas que admite Hostify, con la etiqueta en español. */
export const IDIOMAS: { code: string; label: string }[] = [
  { code: 'es', label: 'Español' },
  { code: 'en', label: 'Inglés' },
  { code: 'fr', label: 'Francés' },
  { code: 'de', label: 'Alemán' },
  { code: 'it', label: 'Italiano' },
  { code: 'pt', label: 'Portugués' },
  { code: 'nl', label: 'Neerlandés' },
  { code: 'ru', label: 'Ruso' },
  { code: 'ja', label: 'Japonés' },
  { code: 'zh', label: 'Chino' },
  { code: 'ar', label: 'Árabe' },
  { code: 'ko', label: 'Coreano' },
  { code: 'pl', label: 'Polaco' },
  { code: 'sv', label: 'Sueco' },
  { code: 'da', label: 'Danés' },
  { code: 'no', label: 'Noruego' },
  { code: 'fi', label: 'Finés' },
  { code: 'ca', label: 'Catalán' },
  { code: 'eu', label: 'Euskera' },
  { code: 'gl', label: 'Gallego' },
];

/** Etiqueta visible de cada uno de los 14 campos. */
export const ETIQUETAS_CAMPO: Record<CopyField, string> = {
  name: 'Título',
  summary: 'Resumen',
  space: 'Espacio',
  interaction: 'Interacción con huéspedes',
  notes: 'Notas adicionales',
  neighborhood_overview: 'Descripción del barrio',
  house_rules: 'Reglas de la casa',
  house_manual: 'Manual de la casa',
  checkin_place: 'Lugar de check-in',
  arrival_info: 'Información de llegada',
  transit: 'Transporte público',
  access: 'Acceso',
  directions: 'Direcciones',
};

/**
 * Descripción corta bajo la etiqueta.
 *
 * Son las mismas frases que el backend mete en el prompt (FIELD_DESCRIPTIONS de
 * ai_service.py): si al usuario le decimos aquí lo mismo que la IA lee allí, lo
 * que escribe a mano y lo que genera el modelo salen con el mismo criterio.
 */
export const AYUDA_CAMPO: Record<CopyField, string> = {
  name: 'Título llamativo para el alojamiento (máx. 50 caracteres)',
  summary: 'Resumen breve y atractivo (2-3 frases)',
  space: 'Descripción del espacio interior del alojamiento',
  interaction: 'Nivel de interacción con el huésped (qué puede esperar)',
  notes: 'Notas adicionales útiles para el huésped',
  neighborhood_overview: 'Descripción atractiva del barrio o la zona',
  house_rules: 'Reglas de la casa (claras, amables pero firmes)',
  house_manual: 'Manual de uso de la casa',
  checkin_place: 'Información del lugar de check-in',
  arrival_info: 'Información práctica de llegada',
  transit: 'Transporte público y accesos cercanos',
  access: 'Cómo acceder a la propiedad',
  directions: 'Indicaciones para llegar',
};

/** Opciones de enriquecimiento que se mandan a la IA. */
export interface OpcionesIA {
  incluirAmenities: boolean;
  incluirFotos: boolean;
  instrucciones: string;
}
